import CancelIcon from '@mui/icons-material/Cancel'
import { Autocomplete, Avatar, Box, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { stringAvatar } from 'components/DataGrid/gridOperators'
import { ReactNode, useState } from 'react'

// Base entity type - entities must have at least an id
export interface BaseEntity {
  id?: string
  [key: string]: any
}

// Avatar configuration for entity display
export interface AvatarConfig<T> {
  /** Function to get photo URL from entity */
  getPhotoUrl?: (entity: T) => string | undefined
  /** Function to get display name for avatar initials */
  getName: (entity: T) => string
  /** Background color for avatar (default: theme primary) */
  bgColor?: string
  /** Avatar size in pixels (default: 32) */
  size?: number
}

// "Create new" option configuration
export interface CreateNewConfig {
  /** Enable the "Add new" option when no match found */
  enabled: boolean
  /** Callback when user clicks "Add new" option */
  onCreateNew: (inputValue: string) => void
  /** Custom button label (default: "Add new") */
  buttonLabel?: string
  /** Custom render for the "Add new" button */
  renderCreateButton?: (inputValue: string, onClick: () => void) => ReactNode
}

// Secondary text line configuration
export interface SecondaryTextConfig<T> {
  /** Function to get secondary text from entity */
  getText: (entity: T) => string | undefined
  /** Font size for secondary text (default: 12) */
  fontSize?: number
}

export interface EntityAutocompleteProps<T extends BaseEntity> {
  /** Array of entity options */
  options: T[]
  /** Currently selected value(s) */
  value: T | T[] | null
  /** Callback when selection changes */
  onChange: (value: T | T[] | null) => void
  /** Function to get display label from entity */
  getOptionLabel: (entity: T) => string
  /** Function to get unique key from entity */
  getOptionKey: (entity: T) => string
  /** Text field label */
  label?: string
  /** Allow multiple selection */
  multiple?: boolean
  /** Avatar rendering configuration */
  avatar?: AvatarConfig<T>
  /** Secondary text line configuration */
  secondaryText?: SecondaryTextConfig<T>
  /** "Create new" option configuration */
  createNew?: CreateNewConfig
  /** Custom filter function (default: filters by label) */
  filterFn?: (options: T[], inputValue: string) => T[]
  /** Allow free text input */
  freeSolo?: boolean
  /** Disabled state */
  disabled?: boolean
  /** Full width */
  fullWidth?: boolean
  /** onBlur handler */
  onBlur?: () => void
  /** Custom sx for the TextField */
  textFieldSx?: object
  /** Autocomplete id */
  id?: string
  /** Max tags to show before collapsing (for multiple) */
  maxTags?: number
  /** Placeholder text */
  placeholder?: string
  /** Loading state */
  loading?: boolean
  /** Error state */
  error?: boolean
  /** Helper text */
  helperText?: string
}

// Internal type for "Add new" special option
interface AddNewOption {
  inputValue: string
  specialOption: true
  id: string
}

type OptionWithAddNew<T> = T | AddNewOption

function isAddNewOption<T>(option: OptionWithAddNew<T>): option is AddNewOption {
  return (option as AddNewOption).specialOption === true
}

function EntityAutocomplete<T extends BaseEntity>({
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionKey,
  label,
  multiple = false,
  avatar,
  secondaryText,
  createNew,
  filterFn,
  freeSolo = false,
  disabled = false,
  fullWidth = true,
  onBlur,
  textFieldSx,
  id,
  maxTags = 1,
  placeholder,
  loading = false,
  error = false,
  helperText,
}: EntityAutocompleteProps<T>) {
  const [inputValue, setInputValue] = useState('')

  // Default filter function - filters by label
  const defaultFilterFn = (opts: T[], input: string): T[] => {
    if (!input) return opts
    const lowerInput = input.toLowerCase()
    return opts.filter(opt => getOptionLabel(opt).toLowerCase().includes(lowerInput))
  }

  const activeFilterFn = filterFn || defaultFilterFn

  // Handle selection change
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: OptionWithAddNew<T> | OptionWithAddNew<T>[] | null,
    reason: string
  ) => {
    if (reason === 'selectOption' || reason === 'removeOption' || reason === 'clear') {
      // Filter out any "Add new" options from the value
      if (Array.isArray(newValue)) {
        const filtered = newValue.filter(v => !isAddNewOption(v)) as T[]
        onChange(filtered)
      } else if (newValue && !isAddNewOption(newValue)) {
        onChange(newValue as T)
      } else if (!newValue) {
        onChange(null)
      }
    }
  }

  // Handle tag removal (for multiple selection)
  const handleRemoveTag = (entityKey: string) => {
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter(v => getOptionKey(v) !== entityKey)
      onChange(newValue)
    }
  }

  // Filter options with "Add new" option injection
  const filterOptions = (opts: OptionWithAddNew<T>[], { inputValue: input }: { inputValue: string }) => {
    // Filter actual options
    const filtered = activeFilterFn(opts.filter(o => !isAddNewOption(o)) as T[], input) as OptionWithAddNew<T>[]

    // Add "create new" option if enabled and input doesn't match existing
    if (
      createNew?.enabled &&
      input !== '' &&
      !filtered.some(opt => !isAddNewOption(opt) && getOptionLabel(opt as T).toLowerCase() === input.toLowerCase())
    ) {
      filtered.unshift({
        inputValue: input,
        specialOption: true,
        id: `add-new-${input}`,
      } as AddNewOption)
    }

    return filtered
  }

  // Render individual option
  const renderOption = (props: React.HTMLAttributes<HTMLLIElement> & { key: string }, option: OptionWithAddNew<T>) => {
    const { key, ...otherProps } = props

    // Handle "Add new" option
    if (isAddNewOption(option)) {
      if (createNew?.renderCreateButton) {
        return (
          <Box component="li" key={key} {...otherProps}>
            {createNew.renderCreateButton(option.inputValue, () => createNew.onCreateNew(option.inputValue))}
          </Box>
        )
      }
      return (
        <Box
          component="li"
          key={key}
          {...otherProps}
          onClick={() => createNew?.onCreateNew(option.inputValue)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Typography color="primary" fontWeight={500}>
            + {createNew?.buttonLabel || 'Add'} "{option.inputValue}"
          </Typography>
        </Box>
      )
    }

    // Handle regular entity option
    const entityKey = getOptionKey(option)
    const entityLabel = getOptionLabel(option)

    // With avatar
    if (avatar) {
      const photoUrl = avatar.getPhotoUrl?.(option)
      const name = avatar.getName(option)
      const avatarSize = avatar.size || 32

      return (
        <Box
          component="li"
          key={`${key}-${entityKey}`}
          {...otherProps}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            p: 0.5,
            gap: 2,
          }}
        >
          {photoUrl ? (
            <Avatar src={photoUrl} alt={name} sx={{ width: avatarSize, height: avatarSize }} />
          ) : (
            <Avatar
              {...stringAvatar(name, { fontSize: avatarSize * 0.4 })}
              sx={{
                bgcolor: avatar.bgColor || 'rgb(255, 188, 220)',
                width: avatarSize,
                height: avatarSize,
              }}
            />
          )}
          <Stack sx={{ justifyContent: 'center', flexGrow: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{entityLabel}</Typography>
            {secondaryText && (
              <Typography sx={{ fontSize: secondaryText.fontSize || 12, color: 'text.secondary' }}>
                {secondaryText.getText(option)}
              </Typography>
            )}
          </Stack>
        </Box>
      )
    }

    // Simple text option (no avatar)
    return (
      <Box component="li" key={`${key}-${entityKey}`} {...otherProps}>
        <Stack>
          <Typography>{entityLabel}</Typography>
          {secondaryText && (
            <Typography sx={{ fontSize: secondaryText.fontSize || 12, color: 'text.secondary' }}>
              {secondaryText.getText(option)}
            </Typography>
          )}
        </Stack>
      </Box>
    )
  }

  // Render tags for multiple selection
  const renderTags = (tagValue: T[], getTagProps: any) => {
    const firstTag = tagValue[0]
    const additionalTags = tagValue.slice(1, maxTags)
    const overflowTags = tagValue.slice(maxTags)
    const overflowCount = overflowTags.length

    return (
      <>
        {firstTag && (
          <Chip
            key={getOptionKey(firstTag)}
            label={getOptionLabel(firstTag)}
            onDelete={() => handleRemoveTag(getOptionKey(firstTag))}
            deleteIcon={<CancelIcon />}
            size="small"
          />
        )}
        {additionalTags.map(tag => (
          <Chip
            key={getOptionKey(tag)}
            label={getOptionLabel(tag)}
            onDelete={() => handleRemoveTag(getOptionKey(tag))}
            deleteIcon={<CancelIcon />}
            size="small"
          />
        ))}
        {overflowCount > 0 && (
          <Tooltip title={overflowTags.map(tag => getOptionLabel(tag)).join(', ')}>
            <Chip
              label={`+${overflowCount}`}
              size="small"
              onDelete={() => {
                // Remove all overflow tags
                if (Array.isArray(value)) {
                  const newValue = value.slice(0, maxTags)
                  onChange(newValue)
                }
              }}
            />
          </Tooltip>
        )}
      </>
    )
  }

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      options={options as OptionWithAddNew<T>[]}
      getOptionLabel={option => {
        if (isAddNewOption(option)) return option.inputValue
        return getOptionLabel(option as T)
      }}
      value={value as OptionWithAddNew<T> | OptionWithAddNew<T>[] | null}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, val) => {
        if (isAddNewOption(option) || isAddNewOption(val)) return false
        return getOptionKey(option as T) === getOptionKey(val as T)
      }}
      renderOption={renderOption}
      renderTags={multiple ? renderTags : undefined}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          sx={textFieldSx}
        />
      )}
      fullWidth={fullWidth}
      disabled={disabled}
      freeSolo={freeSolo}
      loading={loading}
      onBlur={onBlur}
    />
  )
}

export default EntityAutocomplete
